import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config/apiconfig";
import { Download, Upload, Trash2 } from "lucide-react";

const API_URL = `${API_BASE_URL}/api`;

const ResignationDetailsPage = () => {
  const { empNo } = useParams();
  const navigate = useNavigate();
  const [resignation, setResignation] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const buildHeaders = () => ({
    "user-id": localStorage.getItem("token"),
    "user-type": localStorage.getItem("userType"),
    "company-id": localStorage.getItem("companyId"),
  });

  useEffect(() => {
    fetchResignation();
    fetchFiles();
    // eslint-disable-next-line
  }, [empNo]);

  const fetchResignation = async () => {
    try {
      const res = await axios.get(`${API_URL}/resignations/${empNo}`, { headers: buildHeaders() });
      setResignation(res.data);
    } catch (e) {
      setError("Failed to load resignation details");
    }
  };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/empdocs/${empNo}/list`, { headers: buildHeaders() });
      setFiles(res.data);
    } catch (e) {
      setError("Failed to load files");
    }
  };

  const handleUpload = async (e) => {
    const filesToUpload = e.target.files;
    if (!filesToUpload || filesToUpload.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      for (let file of filesToUpload) formData.append("docs", file);
      await axios.post(`${API_URL}/empdocs/${empNo}/upload`, formData, {
        headers: { ...buildHeaders(), "Content-Type": "multipart/form-data" },
      });
      fetchFiles();
    } catch (e) {
      setError("Upload failed");
    }
    setUploading(false);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API_URL}/empdocs/${empNo}/delete/${encodeURIComponent(filename)}`, {
        headers: buildHeaders(),
      });
      fetchFiles();
    } catch (e) {
      setError("Delete failed");
    }
  };

  const handleDownload = (filename) => {
    axios
      .get(`${API_URL}/empdocs/${empNo}/download/${encodeURIComponent(filename)}`, {
        headers: buildHeaders(),
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError("Download failed"));
  };

  const handleDownloadZip = () => {
    axios
      .get(`${API_URL}/empdocs/${empNo}/download-all`, {
        headers: buildHeaders(),
        responseType: "blob",
      })
      .then((response) => {
        const disposition = response.headers["content-disposition"] || "";
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const filename = match ? match[1] : `${empNo}-documents.zip`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setError("Download failed"));
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-slate-800 to-slate-600 py-10 px-2">
      <div className="max-w-5xl mx-auto rounded-2xl shadow-xl bg-white/90 backdrop-blur-md">
        <div className="rounded-t-2xl bg-gradient-to-r from-indigo-700 to-purple-700 px-8 py-5 flex flex-col sm:flex-row items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="text-white hover:underline text-sm mb-2 sm:mb-0">
              ← Back
            </button>
            <h2 className="text-2xl font-bold text-white">Resignation Details</h2>
            <div className="mt-1 text-indigo-100 text-lg font-medium">
              <b>{resignation?.name || ""}</b> ({empNo})
            </div>
          </div>
          <div className="flex gap-3 mt-4 sm:mt-0">
            <button onClick={handleDownloadZip} className="bg-blue-600 hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-semibold flex items-center shadow">
              <Download className="mr-2" size={18} /> Download All as Zip
            </button>
            <label className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center cursor-pointer shadow">
              <Upload className="mr-2" size={18} /> Upload Files
              <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        {error && <div className="bg-red-100 text-red-700 px-4 py-2 font-medium">{error}</div>}

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
            <h3 className="text-lg font-bold text-gray-700 mb-3">Snapshot</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <div><b>Name:</b> {resignation?.name || '—'}</div>
              <div><b>Email:</b> {resignation?.email || '—'}</div>
              <div><b>Department:</b> {resignation?.departmentName || resignation?.department || '—'}</div>
              <div><b>Designation:</b> {resignation?.designation || '—'}</div>
              <div><b>Join Date:</b> {resignation?.joinDate ? String(resignation.joinDate).slice(0,10) : '—'}</div>
              <div><b>EPF:</b> {resignation?.epfNumber || '—'}</div>
              <div><b>Reports To:</b> {resignation?.reportsToName || resignation?.reportsTo || '—'}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
            <h3 className="text-lg font-bold text-gray-700 mb-3">Resignation</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <div><b>Reason:</b> {resignation?.resign_reason || '—'}</div>
              <div><b>Leaving Date:</b> {resignation?.leaving_date ? String(resignation.leaving_date).slice(0,10) : '—'}</div>
              <div><b>Resign Date:</b> {resignation?.resign_date ? String(resignation.resign_date).slice(0,10) : '—'}</div>
              <div><b>Announced Date:</b> {resignation?.announced_date ? String(resignation.announced_date).slice(0,10) : '—'}</div>
              <div><b>Notes:</b> {resignation?.notes || '—'}</div>
              <div><b>Recorded:</b> {resignation?.created_at ? String(resignation.created_at).slice(0,19).replace('T',' ') : '—'}</div>
            </div>
          </div>
        </div>

        <div className="px-8 pb-8">
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-700">All Documents</h3>
              <span className="text-sm text-gray-500">{files.length} file{files.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-left">File Name</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="p-4 text-center text-gray-400">No documents found.</td>
                    </tr>
                  ) : (
                    files.map((filename) => (
                      <tr key={filename} className="border-t hover:bg-slate-50">
                        <td className="p-3 font-mono text-slate-900">{filename}</td>
                        <td className="p-3 flex gap-2">
                          <button onClick={() => handleDownload(filename)} className="bg-blue-500 hover:bg-blue-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center shadow">
                            <Download size={16} className="mr-1" /> Download
                          </button>
                          <button onClick={() => handleDelete(filename)} className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center shadow">
                            <Trash2 size={16} className="mr-1" /> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResignationDetailsPage;
